/* CompareViewComponent.js */

"use strict";
const EventEmitter = require("events").EventEmitter;
const Renderer = require("./CompareViewRenderer");
const ViewMode = require("./ViewModeComponent");
const _ = require("lodash");
const jsDiff = require("diff");

class CompareViewComponent extends EventEmitter {
    constructor(
        logger,
        gameEditor,
        viewState,
        activityMonitor,
        io,
        userSession,
        userService,
        storageFactory,
        MUF
    ) {
        super();
        this._logger = logger;
        this._io = io;
        this._gameEditor = gameEditor;
        this._viewState = viewState;
        this._diffString = undefined;
        this._auditPolicies = [];
        this._modalUserFeedback = MUF;
        this._selectedPath = "";

        this._diffTooLarge = false;

        this._activeOrgId = userSession.activeOrganization.id;
        this._userSession = userSession;
        this._userService = userService;
        this._activityMonitor = activityMonitor;
        this._jsonDiff = false;

        this._storageFactory = storageFactory;
        this._userDataStorage = this._storageFactory.getRemoteStorageInstance(
            "view",
            "audit_policy_compare"
        );

        this._isJsonView = false;

        this._viewMode = new ViewMode(this._logger, this._userSession, storageFactory);

        this._value = {a: undefined, b: undefined};
        this._registerListeners();
        this._renderer = new Renderer(this);

        /**
         * wrapper to make all the calls of async function 'fn' promise chained. like fn(1);fn(2);fn(3) <=> fn(1)->fn(2)->fn(3)
         * @param {function} fn - async function to be chained
         * @param {Promise} [prevPromise=Promise.resolve()] - initial successful promise to resolve first call
         * @returns {function(...[*]): (*|Promise)}
         */

        const selfPromisedWrapper = (fn, prevPromise = Promise.resolve()) => {
            return (...args) => {
                prevPromise = prevPromise.then(() => fn(...args));
                return prevPromise;
            };
        };
        this.setValueA = selfPromisedWrapper(this.setValue.bind(this, "a"));
        this.setValueB = selfPromisedWrapper(this.setValue.bind(this, "b"));

        this.withTheVeil = this.withTheVeil([]); //init withTheVeil wrapper

        this._activityMonitor.maxInactivityPolls = this._activityMonitor.maxInactivityPolls * 3;
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS   */
    /*******************************************/

    _registerListeners() {
        this._viewMode.on(
            "viewModeChanged",
            (this._onViewModeChanged = this._onViewModeChanged.bind(this))
        );
    }

    _unregisterListeners() {
        this._viewMode.removeListener("viewModeChanged", this._onViewModeChanged);
    }

    _generateDiffString() {
        let a_array = Array.from(
            Object.entries(this.jsonToText(_.get(this._value, "a.data", ""))),
            ([key, value]) => `${key}:${value || ""}`
        );
        a_array.sort();
        let a_policy = a_array.join("\n");

        let b_array = Array.from(
            Object.entries(this.jsonToText(_.get(this._value, "b.data", ""))),
            ([key, value]) => `${key}:${value || ""}`
        );
        b_array.sort();
        let b_policy = b_array.join("\n");

        if (a_policy && b_policy) {
            this._policyA = _.get(this._value, "a.data", "");
            this._policyB = this.policyB || {};
            this._getTextDiff(a_policy, b_policy, "");
        } else {
            this._diffString = undefined;
        }

        this.emit("Diff");
    }

    _getTextDiff(a_policy = "", b_policy = "") {
        let context = this.viewMode.compactView === true ? 2 : this._getMax(a_policy, b_policy);

        let diffStr = jsDiff.createTwoFilesPatch(null, null, a_policy, b_policy, null, null, {
            context: context,
        });
        this._diffString = diffStr;
    }

    _getMax(a, b) {
        let a_len = a.match(/.*\r?\n|.+$/g).length;
        let b_len = b.match(/.*\r?\n|.+$/g).length;
        return Math.max(a_len, b_len);
    }

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS     */
    /* START PUBLIC COMPONENT MODEL METHODS    */
    /*******************************************/

    get io() {
        return this._io;
    }

    get diffString() {
        return this.getDiffString();
    }

    getDiffString() {
        return this._diffString;
    }

    get contextLineCount() {
        return this.getContextLineCount();
    }

    get userSession() {
        return this._userSession;
    }

    get userService() {
        return this._userService;
    }

    get viewMode() {
        return this._viewMode;
    }

    /**
     * Set compared values, and show loader
     * @param {Object} firstEntity
     * @param {Object} secondEntity
     * @param {boolean} emit - if we need to emit event
     */

    setValues(firstEntity, secondEntity, emit = true) {
        //this.policyA = this.jsonToText(firstEntity.data);
        this.policyB = this.jsonToText(secondEntity.data);
        this._generateDiffString();
        this.emit("Diff");

        const setValuesProm = Promise.all([
            //firstEntity && this.setValueA(firstEntity, emit),
            secondEntity && this.setValueB(secondEntity, emit),
        ]);

        return this.withTheVeil(setValuesProm).catch(
            console.warn.bind(console, "withTheVeil error:")
        );
    }

    /**
     * function wrapper to show Veil and keep it shown until all promises are resolved (and newly added promises as well)
     * @param {Array} [promiseSet=[]] - initial empty array
     * @returns {function(*=): (*|Promise)}
     */

    withTheVeil(promiseSet = []) {
        const showVeil = () =>
            this._modalUserFeedback.displayLoading({message: "Loading comparison"});
        const hideVeil = () => this._modalUserFeedback.hideDisplayed();

        const getPromiseSet = () => promiseSet;
        const addToSet = (prom) => (promiseSet = promiseSet.concat(prom));
        const resetSet = () => (promiseSet = []);

        return (prom) => {
            let promiseSet = getPromiseSet();
            if (promiseSet.length === 0) {
                promiseSet = addToSet(showVeil());
            }
            promiseSet = addToSet(prom);
            return Promise.all(promiseSet).then(() => {
                if (promiseSet === getPromiseSet()) {
                    resetSet();
                    return hideVeil().then(() => ({done: true}));
                }
                return Promise.resolve({done: false});
            });
        };
    }

    setValue(side, val, emit = true) {
        this._value[side] = val;
        if (_.isUndefined(this._value[side].capture)) {
            if (emit) {
                this.emit("Value", side, this._value[side]);
            }
        }
        if (!this._jsonDiff) {
            this._generateDiffString();
        } else {
            this._generateJSONDiffString();
        }
        if (emit) {
            this.emit("Value", side, this._value[side]);
        }
    }

    getValue(side) {
        return this._value[side];
    }

    getOptions() {
        return this._auditPolicies;
    }

    get renderer() {
        return this.getRenderer();
    }

    getRenderer() {
        return this._renderer;
    }

    get gameEditor() {
        return this._gameEditor;
    }

    get logger() {
        return this._logger;
    }

    whenModelIsReady() {
        this._viewType = this._userDataStorage.getData("viewType", true);
        this._params = this._viewState.params;
        if (this._viewType === "json") {
            this._isJsonView = true;
        }
        return this.viewMode
            .whenStorageIsReady()
            .then(() => {
                this.selectedAuditId = this._params.activeCommit;
                this._gameEditor.emit("ClearViewState");

                return Promise.resolve();
            })
            .then(() => {
                this.isJSONAudit = false;
                //some  default values for now
                this._value.a = {commitHash: "Active Policy", data: this._params.policy};
                this._value.b = this._params.compareVals[0];
                //this.setValues(this._value.a, this._value.b);
                this._compareVals = this._params.compareVals;

                return this._buildViewModel(this._compareVals);
            })
            .catch((err) => {
                this._logger.warn({error: err}, "CompareViewComponent.whenModelIsReady() error.");
            });
    }

    shutdown() {
        this._unregisterListeners();
        //reset session timeout to normal
        this._activityMonitor.maxInactivityPolls =
            this._activityMonitor._options.maxInactivityPolls;

        let promises = [this._viewMode.shutdown(), this._renderer.shutdown()];
        return Promise.all(promises).then(
            () => {
                this._logger = undefined;
                this._gameEditor = undefined;
                this._renderer = undefined;
                this._io = undefined;
                this._gameEditor = undefined;
                this._viewMode = undefined;
                this._diffString = undefined;
                this._selectedPath = undefined;
                this._auditPolicies = undefined;
                this._value = undefined;
                this._activityMonitor = undefined;
            },
            (err) => {
                this._logger.warn({error: err}, "CompareViewComponent.shutdown() error.");
            }
        );
    }

    /*******************************************/
    /* END COMPONENT MODEL METHODS             */
    /* START HANDLER METHODS                   */
    /*******************************************/

    _onViewModeChanged() {
        this.withTheVeil([this._generateDiffString()]);
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE METHODS                   */
    /*******************************************/

    _buildViewModel(cache) {
        const ignorePropsList = [
            "lastCommitHash",
            "createdBy",
            "createdIp",
            "createdOn",
            "id",
            "tid",
            "updatedBy",
            "updatedIp",
            "updatedOn",
        ];

        //remove lastCommitHash from policy
        delete this._params.policy.lastCommitHash;

        this._auditPolicies = [];
        Object.keys(cache).forEach((id) => {
            const entry = cache[id];
            if (!entry || !entry.data) {
                return;
            }

            if (this._params.activeCommit && this._params.activeCommit === entry.commitHash) {
                this._value.b = entry;
            }

            const timestamp = entry.data.updatedOn ? new Date(entry.data.updatedOn) : null;
            const hash = entry.commitHash;
            let localDateTime = "Unknown";
            if (timestamp && _.isDate(timestamp)) {
                localDateTime =
                    timestamp.toLocaleDateString() + " " + timestamp.toLocaleTimeString();
            }

            // Remove all git style values from git policies from compareVals
            for (const prop of ignorePropsList) {
                if (entry.data) {
                    delete entry.data[prop];
                }
            }

            this._auditPolicies.push({
                label: `${localDateTime} (${hash})`,
                value: {
                    id: hash,
                    name: `${localDateTime} (${hash})`,
                    timestamp: timestamp,
                },
            });
        });

        if (_.isEmpty(this._params.compareVals)) {
            this.renderer._showDefaultMessageDisplay();
            return;
        }
        if (
            _.isEmpty(this._value.b) &&
            Array.isArray(this._params.compareVals) &&
            this._params.compareVals.length > 1 &&
            this._params.compareVals[1] &&
            this._params.compareVals[1].data
        ) {
            this._value.b = this._params.compareVals[1].data;
        }
        if (
            _.isEmpty(this._value.a) &&
            Array.isArray(this._params.compareVals) &&
            this._params.compareVals.length > 0 &&
            this._params.compareVals[0] &&
            this._params.compareVals[0].data
        ) {
            this._value.a = this._params.compareVals[0].data;
        }

        // set Policy values
        this.policyA =
            this._value.a && this._value.a.data ? this.jsonToText(this._value.a.data) : {};

        return this.setValues(this._value.a, this._value.b, false);
    }

    jsonToText(policy) {
        // just flatten for now, but we may do more to process the policy objects in the future
        const flatten = function (jsonobj) {
            const result = {};
            const recurse = (obj, path = "") => {
                if (Array.isArray(obj)) {
                    obj.forEach((item, index) => {
                        const newPath = path ? `${path}[${index}]` : `[${index}]`;
                        if (item !== null && typeof item === "object") {
                            recurse(item, newPath);
                        } else {
                            result[newPath] = item;
                        }
                    });
                } else if (obj !== null && typeof obj === "object") {
                    for (const key in obj) {
                        const newPath = path ? `${path}.${key}` : key;
                        const value = obj[key];
                        if (value !== null && typeof value === "object") {
                            recurse(value, newPath);
                        } else {
                            result[newPath] = value;
                        }
                    }
                } else {
                    result[path] = obj;
                }
            };
            recurse(jsonobj);
            return result;
        };
        return flatten(policy);
    }

    /*******************************************/
    /* END PRIVATE METHODS                     */
    /* START PUBLIC API METHODS                */
    /*******************************************/

    getViewModel() {
        return this._auditPolicies;
    }

    switchView(switchTo, params) {
        let _params = {isSubView: false};
        if (params && params.commitHash) {
            _params = Object.assign({}, _params, {commitHash: params.commitHash});
        }
        this._gameEditor.setActiveView(switchTo, [], _params);
    }

    /*******************************************/
    /* END PUBLIC API METHODS                  */
    /*******************************************/
}

module.exports = CompareViewComponent;

CompareViewComponent.$inject = [
    "$teve.logger",
    "gameEditor.gameEditor",
    "gameEditor.viewState",
    "$teve.activityMonitor",
    "$teve.io",
    "$teve.userSession",
    "$teve.services.User",
    "$teve.storageFactory",
    "$teve.ui.userFeedback",
];

CompareViewComponent.$displayDetails = {
    type: "View",
    name: "Comparison",
    icon: "configdrift.compare",
    title: "Comparisons",
    shown: true,
    order: 8,
};
